import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import { calculateCalendarDuration, elapsedEnd } from './calendarDuration';
import { toLocalDay } from './dateOnly';

dayjs.extend(relativeTime);
dayjs.extend(duration);

/**
 * Gets a human-readable relative time (e.g., "2 days ago", "in 3 months")
 * @param {string|Date} date - The date to compare
 * @param {string|Date|null} fromDate - The reference date (defaults to now)
 * @returns {string} Relative time string
 */
export const getRelativeTime = (date, fromDate = null) => {
  if (!date) return '';
  
  const compareDate = dayjs(fromDate || new Date());
  return dayjs(date).from(compareDate);
};

/**
 * Gets a human-readable duration from today to a target date in weeks and days
 * @param {string|Date} targetDate - The target date to calculate duration to
 * @returns {string} Duration string in format "X weeks and Y days from now" or "Y days from now"
 */
export const getDurationFromNow = (targetDate) => {
  if (!targetDate) return '';
  
  const today = dayjs().startOf('day');
  // ADR-197: a date-only target (deadline) is its calendar day, not a local instant
  const target = toLocalDay(targetDate);
  if (!target) {
    return '';
  }
  const diff = target.diff(today);
  const duration = dayjs.duration(diff);
  
  const weeks = Math.floor(duration.asDays() / 7);
  const days = Math.floor(duration.asDays() % 7);
  
  if (weeks === 0) {
    return `${days} day${days !== 1 ? 's' : ''} from now`;
  }
  
  return `${weeks} week${weeks !== 1 ? 's' : ''} ${days > 0 ? `and ${days} day${days !== 1 ? 's' : ''}` : ''} from now`;
};

/**
 * Calculates the duration between two dates in a human-readable format.
 * ADR-128: calendar-aware and inclusive (the start date is day 1), with the
 * month-end clamp — the same rules as the API's timeInOrganization.
 * ADR-196: elapsed time only — the interval ends at the earlier of the end
 * date and today, so a future end date never counts days not yet spent.
 * @param {string|Date} startDate - The start date
 * @param {string|Date|null} endDate - The end date (optional, defaults to today if null)
 * @returns {string} Duration in format "X years Y months Z days"
 */
export const calculateDuration = (startDate, endDate = null) => {
  const { years, months, days } = calculateCalendarDuration(startDate, elapsedEnd(endDate, new Date()));

  const parts = [];

  if (years > 0) {
    parts.push(`${years} year${years !== 1 ? 's' : ''}`);
  }

  if (months > 0) {
    parts.push(`${months} month${months !== 1 ? 's' : ''}`);
  }

  if (days > 0 || parts.length === 0) {
    parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  }

  return parts.join(' ');
};

/**
 * Formats an INSTANT into DD/MM/YYYY in the viewer's local time (e.g. createdAt,
 * updatedAt). ADR-197: do NOT use for date-only values (start/end/hire/resign/
 * effective dates, deadlines) — those must use formatDateOnly from
 * ./dateOnly, or viewers west of UTC see the previous day.
 * @param {string|Date} dateString - The instant to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  return dayjs(dateString).format('DD/MM/YYYY');
};

/**
 * Formats a time duration object into a human-readable string
 * @param {Object} timeDuration - Object containing years, months, and days
 * @param {number} timeDuration.years - Number of years
 * @param {number} timeDuration.months - Number of months
 * @param {number} timeDuration.days - Number of days
 * @param {string} suffix - Optional suffix to append (e.g., "on the Project", "in the Organization")
 * @returns {string} Formatted duration string (e.g., "2 years, 3 months, 5 days on the Project")
 */
export const formatTimeDuration = (timeDuration, suffix = '') => {
  if (!timeDuration) return '';
  
  const { years = 0, months = 0, days = 0 } = timeDuration;
  
  // Check if all values are zero
  if (years === 0 && months === 0 && days === 0) {
    return 'Not started yet';
  }
  
  const parts = [];
  
  if (years > 0) {
    parts.push(`${years} year${years !== 1 ? 's' : ''}`);
  }
  
  if (months > 0) {
    parts.push(`${months} month${months !== 1 ? 's' : ''}`);
  }
  
  if (days > 0) {
    parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  }
  
  let result = parts.join(', ');
  
  if (suffix) {
    result += ` ${suffix}`;
  }
  
  return result;
}; 